/**
 * /pulse/:slug — Neighborhood Pulse detail page.
 *   Overlay of Civic Reports + Nature Observations on a Leaflet map,
 *   Livability composite score (Community · Green · Safety · Walkability),
 *   Top civic categories, top nature categories, and quick CTAs.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { http } from "@/lib/api";
import {
  MapPin, Users, Activity, Leaf, AlertTriangle, ChevronRight,
  Camera, Flag, Sparkles,
} from "lucide-react";
import "leaflet/dist/leaflet.css";
import PulseItemsFeed from "@/components/PulseItemsFeed";

export default function NeighborhoodDetail() {
  const params = useParams();
  // React Router's `*` catch-all comes in as params["*"]; fall back to slug.
  const slug = params["*"] || params.slug || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    http.get(`/neighborhood/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Not found"));
  }, [slug]);

  if (err) return (
    <div className="max-w-3xl mx-auto py-24 text-center px-6" data-testid="pulse-notfound">
      <h1 className="font-display text-3xl">Neighborhood not found</h1>
      <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>{err}</p>
      <Link to="/pulse" className="wofy-btn-primary inline-flex mt-8">Browse all neighbourhoods</Link>
    </div>
  );
  if (!data) return <div className="max-w-3xl mx-auto py-24 text-center" style={{ color: "var(--wofy-text-soft)" }}>Loading pulse…</div>;

  const { locality: L, civic, nature, livability } = data;
  const accent = L.accent || "var(--wofy-primary)";
  const admin = [L.city, L.district, L.state, L.country].filter(Boolean).join(" · ");
  const pageTitle = `${L.city} Neighborhood Pulse — Social Pulse`;
  const metaDesc = `Daily civic reports and nature observations across ${L.city}. ${L.banner_headline}`;

  return (
    <div data-testid="pulse-detail">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDesc} />
      </Helmet>

      {/* Locality banner */}
      <section
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${accent}12, ${accent}05)` }}
        data-testid="pulse-banner"
      >
        <div className="max-w-6xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline" style={{ color: accent }}>{admin}</div>
            <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-tight" data-testid="pulse-headline">
              {L.banner_headline}
            </h1>
            <p className="mt-4 text-base leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
              {L.banner_sub}
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/civic" className="wofy-btn-primary text-sm" data-testid="pulse-cta-civic">
                <Flag className="w-4 h-4" /> Report an issue
              </Link>
              <Link to="/nature?section=nature&tab=observations" className="wofy-btn-ghost text-sm" data-testid="pulse-cta-observe">
                <Camera className="w-4 h-4" /> Log an observation
              </Link>
              <Link to="/opportunities" className="wofy-btn-ghost text-sm" data-testid="pulse-cta-volunteer">
                <Sparkles className="w-4 h-4" /> Volunteer today
              </Link>
            </div>
            <p className="mt-4 text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
              {L.banner_cta}
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
            <img src={L.banner_image} alt={L.city} className="w-full h-72 md:h-80 object-cover" data-testid="pulse-banner-image" />
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-12 space-y-10">
        {/* At-a-glance tiles */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pulse-tiles">
          <Tile icon={Users} label="Population" value={L.population_formatted} />
          <Tile icon={Activity} label={`Civic reports (${data.window_days}d)`} value={civic.in_window} />
          <Tile icon={Leaf} label={`Nature observations (${data.window_days}d)`} value={nature.in_window} />
          <Tile icon={AlertTriangle} label="Open high-severity" value={civic.high_severity_open}
                emphasize={civic.high_severity_open > 0} />
        </div>

        {/* Livability + map */}
        <div className="grid lg:grid-cols-3 gap-6">
          <LivabilityCard livability={livability} accent={accent} />
          <PulseMap locality={L} civic={civic} nature={nature} />
        </div>

        {/* By category rows */}
        <div className="grid md:grid-cols-2 gap-6">
          <CivicCategories civic={civic} accent={accent} />
          <NatureCategories nature={nature} accent={accent} />
        </div>

        {/* Recent nature feed */}
        {nature.pins.length > 0 && (
          <section data-testid="pulse-recent-obs">
            <div className="overline mb-4">RECENT NATURE IN {L.city.toUpperCase()}</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {nature.pins.slice(0, 8).map((o) => (
                <Link key={o.id} to="/nature?section=nature&tab=observations" className="block aspect-square overflow-hidden rounded-lg group">
                  {o.image_url ? (
                    <img src={o.image_url} alt={o.common_name || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: "var(--wofy-surface)" }}>
                      <Leaf className="w-6 h-6 opacity-30" />
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </section>
        )}

        <p className="text-xs" style={{ color: "var(--wofy-text-soft)" }}>
          Data window: last {data.window_days} days. {livability.disclaimer}{" "}
          <Link to="/pulse" className="underline">See other neighbourhoods →</Link>
        </p>

        <PulseItemsFeed
          slug={slug}
          city={L.city}
          state={L.state}
          country={L.country}
          lat={L.lat}
          lng={L.lng}
          canAdd={true}
        />
      </div>
    </div>
  );
}


function Tile({ icon: Icon, label, value, emphasize }) {
  return (
    <div className="wofy-card !py-5" data-testid={`pulse-tile-${label.split(" ")[0].toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color: emphasize ? "#B23A3A" : "var(--wofy-primary)" }} />
        <div className="overline text-[10px]">{label}</div>
      </div>
      <div className="font-display text-4xl mt-2" style={{ color: emphasize ? "#B23A3A" : "inherit" }}>{value ?? "0"}</div>
    </div>
  );
}


function LivabilityCard({ livability, accent }) {
  const rings = [
    { key: "community_activity", label: "Community Activity" },
    { key: "green_space", label: "Green Space" },
    { key: "safety", label: "Safety" },
    { key: "walkability", label: "Walkability" },
  ];
  const score = livability.score;
  return (
    <div className="wofy-card" data-testid="pulse-livability">
      <div className="overline">LIVABILITY PULSE</div>
      <div className="mt-3 flex items-center gap-4">
        <div className="w-24 h-24 rounded-full flex items-center justify-center relative" style={{ background: `${accent}18`, border: `4px solid ${accent}` }}>
          <div className="font-display text-4xl" style={{ color: accent }} data-testid="pulse-livability-score">{score}</div>
          <span className="absolute -bottom-1 text-[10px] font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>/ 100</span>
        </div>
        <div className="text-xs leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
          A composite of Social Pulse activity. Higher = more engaged residents.
        </div>
      </div>
      <ul className="mt-5 space-y-3">
        {rings.map((r) => (
          <li key={r.key} data-testid={`pulse-sub-${r.key}`}>
            <div className="flex justify-between text-xs">
              <span>{r.label}</span>
              <span className="font-mono">{livability.sub_scores[r.key]}</span>
            </div>
            <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--wofy-surface)" }}>
              <div className="h-full" style={{ width: `${livability.sub_scores[r.key]}%`, background: accent }} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}


function CivicCategories({ civic, accent }) {
  return (
    <div className="wofy-card" data-testid="pulse-civic-cats">
      <div className="flex items-center justify-between">
        <div className="overline">CIVIC ISSUES · TOP CATEGORIES</div>
        <Link to="/civic" className="text-[11px] font-mono uppercase hover:underline flex items-center gap-1" style={{ color: accent }}>
          All reports <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      {civic.by_category.length === 0 ? (
        <p className="mt-4 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          No reports logged here yet — <Link to="/civic" className="underline">be the first</Link>.
        </p>
      ) : (
        <ul className="mt-4 space-y-2 text-sm">
          {civic.by_category.map((c) => (
            <li key={c.key} className="flex items-center gap-3">
              <span className="flex-1 truncate">{c.label}</span>
              <span className="font-display text-xl">{c.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function NatureCategories({ nature, accent }) {
  return (
    <div className="wofy-card" data-testid="pulse-nature-cats">
      <div className="flex items-center justify-between">
        <div className="overline">NATURE · TOP CATEGORIES</div>
        <Link to="/nature/discover" className="text-[11px] font-mono uppercase hover:underline flex items-center gap-1" style={{ color: accent }}>
          Explore <ChevronRight className="w-3 h-3" />
        </Link>
      </div>
      {nature.by_category.length === 0 ? (
        <p className="mt-4 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          No observations here yet — <Link to="/nature?section=nature&tab=observations" className="underline">share the first</Link>.
        </p>
      ) : (
        <ul className="mt-4 space-y-2 text-sm">
          {nature.by_category.map((c) => (
            <li key={c.key} className="flex items-center gap-3">
              {c.sample_image ? (
                <img src={c.sample_image} alt="" className="w-8 h-8 rounded object-cover" />
              ) : (
                <div className="w-8 h-8 rounded" style={{ background: "var(--wofy-surface)" }} />
              )}
              <span className="flex-1 truncate capitalize">{(c.key || "other").replace(/_/g, " ")}</span>
              <span className="font-display text-xl">{c.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function PulseMap({ locality, civic, nature }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !containerRef.current) return;
      if (!mapRef.current) {
        mapRef.current = L.map(containerRef.current, {
          center: [locality.lat, locality.lng],
          zoom: 11,
          minZoom: 3,
          worldCopyJump: true,
        });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap",
          maxZoom: 18,
        }).addTo(mapRef.current);
      }
      mapRef.current.setView([locality.lat, locality.lng], 11);
      if (layerRef.current) {
        layerRef.current.clearLayers();
      } else {
        layerRef.current = L.layerGroup().addTo(mapRef.current);
      }
      // Civic pins — red-orange
      (civic.pins || []).forEach((r) => {
        if (r.lat == null || r.lng == null) return;
        L.circleMarker([r.lat, r.lng], {
          radius: 6, weight: 1, color: "#B23A3A", fillColor: "#B23A3A", fillOpacity: 0.75,
        }).bindPopup(
          `<div style="font-family:Georgia,serif;font-size:13px;max-width:220px">
            <div style="font-family:JetBrains Mono,monospace;font-size:10px;color:#B23A3A;text-transform:uppercase;letter-spacing:0.06em">${r.severity || "report"} · ${r.category_label || r.category_key || "civic"}</div>
            <div style="font-weight:600;margin-top:2px">${r.title || "Civic report"}</div>
          </div>`
        ).addTo(layerRef.current);
      });
      // Nature pins — moss green
      (nature.pins || []).forEach((o) => {
        if (o.lat == null || o.lng == null) return;
        L.circleMarker([o.lat, o.lng], {
          radius: 6, weight: 1, color: "#5C6B52", fillColor: "#5C6B52", fillOpacity: 0.75,
        }).bindPopup(
          `<div style="font-family:Georgia,serif;font-size:13px;max-width:220px">
            <div style="font-family:JetBrains Mono,monospace;font-size:10px;color:#5C6B52;text-transform:uppercase;letter-spacing:0.06em">${o.category || "observation"}</div>
            <div style="font-weight:600;margin-top:2px">${o.common_name || "Observation"}</div>
            ${o.image_url ? `<img src="${o.image_url}" style="width:100%;margin-top:6px;border-radius:6px"/>` : ""}
          </div>`
        ).addTo(layerRef.current);
      });
    })();
    return () => { cancelled = true; };
  }, [locality.lat, locality.lng, civic.pins, nature.pins]);

  return (
    <div className="lg:col-span-2 rounded-xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }} data-testid="pulse-map">
      <div ref={containerRef} className="w-full h-[520px]" style={{ background: "var(--wofy-surface)" }} />
      <div className="px-4 py-3 border-t flex items-center gap-4 text-[11px] font-mono uppercase tracking-wider" style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#B23A3A" }} /> Civic reports · {civic.pins.length}</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: "#5C6B52" }} /> Nature obs · {nature.pins.length}</span>
      </div>
    </div>
  );
}
